import { jwtDecode } from 'jwt-decode';
import type { Impersonation } from '../api/types';

export interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

export const parseJwt = <T extends JwtPayload = JwtPayload>(token: string): T | null => {
  try {
    return jwtDecode<T>(token);
  } catch {
    return null;
  }
};

export const getTokenExpiration = (token: string): number | null => {
  const decoded = parseJwt(token);
  return decoded?.exp ?? null;
};

export const isTokenExpired = (token: string): boolean => {
  const exp = getTokenExpiration(token);
  if (!exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return now >= exp;
};

export const getTokenTimeRemaining = (token: string): number => {
  const exp = getTokenExpiration(token);
  if (!exp) return 0;
  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, exp - now);
};

export const getTokenTimeRemainingMs = (token: string): number =>
  getTokenTimeRemaining(token) * 1000;

interface ActClaim {
  id?: string;
  name?: string;
  email?: string;
  sid?: string;
}

/**
 * Reads the `act` claim from the session token. Decode only — the backend
 * verifies the signature on every request, so this is display state, never a
 * security decision.
 */
export const readImpersonation = (token: string | null): Impersonation | null => {
  if (!token) return null;
  const act = parseJwt<JwtPayload & { act?: ActClaim }>(token)?.act;
  if (!act?.id) return null;
  return {
    adminId: act.id,
    adminName: act.name ?? '',
    adminEmail: act.email ?? '',
    sessionId: act.sid ?? '',
  };
};
